import { useCallback, useMemo } from "react";

import type { EvalSample } from "@tsmono/inspect-common/types";
import {
  normalizeSearchPanelState,
  type ModelHistoryController,
  type SearchPanelApi,
  type SearchPanelNavigation,
  type SearchPanelStateController,
  type SearchScope,
  type SearchType,
} from "@tsmono/inspect-components/transcript-search";

import { getApi, useLogDir } from "../../../../app_config";
import {
  kSampleMessagesTabId,
  kSampleTranscriptTabId,
} from "../../../../constants";
import { useStore } from "../../../../state/store";
import { useUserSettings } from "../../../../state/userSettings";
import { useCurrentLogFile } from "../../../routing/currentSelection";
import {
  makeLogsPath,
  sampleEventUrl,
  sampleMessageUrl,
  useLogOrSampleRouteParams,
  useSampleUrlBuilder,
} from "../../../routing/url";

/**
 * inspect_scout's search endpoints (proxied by inspect_ai under /scout/...)
 * are backed by transcripts_view(dir), which:
 *   - accepts a `.eval` file path (file:// prefix stripped) as `dir`
 *   - looks up rows by `transcript_id`, which equals `eval_sample.uuid`
 *
 * Samples without a UUID can't be searched (the human-readable sample.id
 * isn't what the schema indexes by).
 */
const stripFileScheme = (path: string): string =>
  path.startsWith("file://") ? path.slice("file://".length) : path;

/**
 * Fully-resolved input for `SearchPanelSlot`. Either the surrounding sample
 * is searchable and every piece is present, or `useInspectSearchContext`
 * returns null. No half-populated state at the call site.
 */
export interface InspectSearchContext {
  transcriptId: string;
  logFile: string;
  logPath: string;
  sampleId: string | number;
  sampleEpoch: number;
}

/**
 * Resolves everything a search panel needs from a sample + ambient store /
 * route state, or returns null when the sample can't be searched. Callers
 * pass the result straight to `SearchPanelSlot` and use it as the single
 * gate on the toolbar Search button.
 *
 * Requirements: the backend exposes the search endpoints, the sample carries
 * a UUID (the schema indexes by `eval_sample.uuid`), and we can derive an
 * absolute `.eval` path plus a URL-shaped log path for deep links.
 */
export const useInspectSearchContext = (
  sample: EvalSample | undefined
): InspectSearchContext | null => {
  const api = getApi();
  const selectedLogFile = useCurrentLogFile();
  const logDir = useLogDir();
  const { logPath: urlLogPath } = useLogOrSampleRouteParams();

  // Depend on the scalar fields, not the sample object: polling a running
  // sample replaces the object each tick even when these are unchanged.
  const sampleUuid = sample?.uuid;
  const sampleId = sample?.id;
  const sampleEpoch = sample?.epoch;

  return useMemo(() => {
    if (!sampleUuid || sampleId === undefined || sampleEpoch === undefined) {
      return null;
    }
    if (!api.post_search || !api.get_search_result || !api.list_searches) {
      return null;
    }
    // selectedLogFile is the absolute `file://...` URI the backend uses to
    // open the eval log; urlLogPath is sometimes just a filename relative
    // to logDir, so we may have to reconstruct it.
    const logFile = selectedLogFile ?? urlLogPath;
    if (!logFile) return null;
    const logPath = urlLogPath ?? makeLogsPath(logFile, logDir);
    if (!logPath) return null;
    return {
      transcriptId: sampleUuid,
      logFile,
      logPath,
      sampleId,
      sampleEpoch,
    };
  }, [
    api,
    sampleUuid,
    sampleId,
    sampleEpoch,
    selectedLogFile,
    urlLogPath,
    logDir,
  ]);
};

export const getInspectSearchPanelStateKey = ({
  scope,
  logFile,
  transcriptId,
}: {
  scope: SearchScope;
  logFile: string;
  transcriptId: string;
}) => `inspect-search-panel:${logFile}:${transcriptId}:${scope}`;

export const useInspectSearchApi = (
  logFile: string,
  transcriptId: string
): SearchPanelApi | null => {
  const api = getApi();
  return useMemo(() => {
    const { post_search, get_search_result, list_searches } = api;
    if (!post_search || !get_search_result || !list_searches || !transcriptId) {
      return null;
    }
    const transcriptDir = stripFileScheme(logFile);
    return {
      cacheKey: `${transcriptDir}\u0000${transcriptId}`,
      createSearch: (request) =>
        post_search(transcriptDir, transcriptId, request),
      getCachedResult: (searchId, scope) =>
        get_search_result(transcriptDir, transcriptId, searchId, scope),
      listRecentSearches: (searchType: SearchType, count?: number) =>
        list_searches(searchType, count ?? 20),
    };
  }, [api, logFile, transcriptId]);
};

export const useInspectSearchPanelState = ({
  scope,
  logFile,
  transcriptId,
}: {
  scope: SearchScope;
  logFile: string;
  transcriptId: string;
}): SearchPanelStateController => {
  const key = useMemo(
    () => getInspectSearchPanelStateKey({ scope, logFile, transcriptId }),
    [scope, logFile, transcriptId]
  );
  const stored = useStore((s) => s.search.panelStates[key]);
  const setSearchPanelState = useStore(
    (s) => s.searchActions.setSearchPanelState
  );

  const state = useMemo(() => normalizeSearchPanelState(stored), [stored]);
  const setState = useCallback<SearchPanelStateController["setState"]>(
    (updater) => setSearchPanelState(key, updater),
    [key, setSearchPanelState]
  );

  return useMemo(() => ({ state, setState }), [state, setState]);
};

export const useInspectSearchModelHistory = (): ModelHistoryController => {
  const history = useUserSettings((s) => s.searchModelHistory);
  const record = useUserSettings((s) => s.recordSearchModel);
  return useMemo(() => ({ history, record }), [history, record]);
};

export const useInspectSearchNavigation = ({
  logPath,
  sampleId,
  sampleEpoch,
}: {
  logPath: string;
  sampleId: string | number;
  sampleEpoch: number;
}): SearchPanelNavigation => {
  const builder = useSampleUrlBuilder();
  return useMemo(
    () => ({
      getEventUrl: (eventId) =>
        sampleEventUrl(builder, eventId, logPath, sampleId, sampleEpoch),
      getMessageUrl: (messageId) =>
        sampleMessageUrl(
          builder,
          messageId,
          logPath,
          sampleId,
          sampleEpoch,
          kSampleMessagesTabId
        ),
      getEventMessageUrl: (messageId) =>
        sampleMessageUrl(
          builder,
          messageId,
          logPath,
          sampleId,
          sampleEpoch,
          kSampleTranscriptTabId
        ),
    }),
    [builder, logPath, sampleId, sampleEpoch]
  );
};
